import {
  Fragment,
  type PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DateTime } from "luxon";

import { useDebounce } from "@uidotdev/usehooks";
import {
  BellIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHourglass, faRoad } from "@fortawesome/free-solid-svg-icons";

import useLeads from "../../data/useLeads";
import useTrips from "../../data/useTrips";
import { ITruck } from "../../models/Truck";
import useTrucks from "../../data/useTrucks";
import classNames from "../../utils/classNames";
import { ICalendarTrip, ITripsResponse } from "../../models/Trip";
import { ILead, LeadStatus } from "../../models/Lead";
import { INote, ResourceType } from "../../models/Note";
import formatDateTime from "../../utils/formatDateTime";
import { getLeadPostcode } from "../../utils/getLeadLocation";
import { hasCollectionOption } from "../../utils/leadPaymentOption";
import useLeadsFromIds from "../../data/useLeadsFromIds";
import TripSlot from "../../components/tripManager/TripSlot";
import ThirdPartyTripSlot from "../../components/tripManager/ThirdPartyTripSlot";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { IUnavailableDate } from "../../models/UnavailableDate";
import useLeadsWithFilters from "../../data/useLeadsWithFilters";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import CalendarDayMap from "../../components/calendar/CalendarDayMap";
import PrintJobSheet from "../../components/tripManager/PrintJobSheet";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Tooltip from "../../components/shared/Tooltip";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import UnscheduledLeadsPanel from "../../components/tripManager/UnscheduledLeadsPanel";
import TripManagerNotes from "../../components/tripManager/TripManagerNotes";
import useNotification from "../../components/notifications/useNotifications";
import { AssignDriverModal } from "../../components/tripManager/AssignDriverModal";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";

const MIN_TRIP_ROWS = 3;
const THIRD_PARTY_KEY = "__third_party__";
const VEHICLE_COLUMN_WIDTH = 240;
const UNSCHEDULED_PANEL_STORAGE_KEY = "tripManager.showUnscheduledAtTop";
const SHOW_WEEKEND_STORAGE_KEY = "tripManager.showWeekend";

export interface Distances {
  green: string[];
  amber: string[];
  red: string[];
}

type DayStatus =
  | "empty"
  | "none"
  | "partial"
  | "complete"
  | "nearbyGreen"
  | "nearbyAmber"
  | "nearbyRed"
  | "nearbyNone";

const dayStatusDot: Record<DayStatus, string> = {
  empty: "bg-gray-200",
  none: "bg-red-400",
  partial: "bg-amber-400",
  complete: "bg-green-500",
  nearbyGreen: "bg-green-500 ring-2 ring-green-300",
  nearbyAmber: "bg-yellow-500 ring-2 ring-yellow-300",
  nearbyRed: "bg-red-500 ring-2 ring-red-300",
  nearbyNone: "bg-gray-300",
};

const dayStatusPillBg: Partial<Record<DayStatus, string>> = {
  nearbyGreen: "bg-green-100 hover:bg-green-200",
  nearbyAmber: "bg-yellow-100 hover:bg-yellow-200",
  nearbyRed: "bg-red-100 hover:bg-red-200",
  nearbyNone: "bg-gray-100 hover:bg-gray-200",
};

const dayStatusLabel: Record<DayStatus, string> = {
  empty: "No trips scheduled",
  none: "Nothing collected yet",
  partial: "Partially collected",
  complete: "All collected",
  nearbyGreen: "Has a nearby collection (close)",
  nearbyAmber: "Has a nearby collection (medium)",
  nearbyRed: "Has a nearby collection (far)",
  nearbyNone: "No nearby collections",
};

export default function TripManager() {
  const { trucks } = useTrucks();
  const now = DateTime.now();
  const [yearNumber, setYearNumber] = useState<number>(now.weekYear);
  const [weekNumber, setWeekNumber] = useState<number>(now.weekNumber);
  const { trips: calendarTrips, create } = useTrips(yearNumber, weekNumber);
  const { setInfo } = usePageNameContext();
  const { distances: allDistances, getAllCollectionNotes } = useLeads(true, "TripManager");
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notesOpen, setNotesOpen] = useState<boolean>(false);
  const [notesDate, setNotesDate] = useState<string | undefined>(undefined);
  const [notesUnavailableDates, setNotesUnavailableDates] = useState<
    IUnavailableDate[]
  >([]);
  const [notesTruck, setNotesTruck] = useState<ITruck | undefined>(undefined);
  const [notesDriverId, setNotesDriverId] = useState<string | undefined>(undefined);
  const [isCollectionNoteLoading, setIsCollectionNoteLoading] =
    useState<boolean>(false);
  const [unbooked, setUnbooked] = useState<ILead[]>([]);
  const [dayLeads, setDayLeads] = useState<ILead[]>([]);
  const [trips, setTrips] = useState<ITripsResponse[]>([]);

  const [addTripConfirm, setAddTripConfirm] = useState<{ date: string; truckId: string; message: string } | null>(null);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [showCollected, setShowCollected] = useState<boolean>(false);
  const [showNotPaid, setShowNotPaid] = useState<boolean>(false);
  const [showUnscheduledAtTop, setShowUnscheduledAtTop] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;

    return window.localStorage.getItem(UNSCHEDULED_PANEL_STORAGE_KEY) === "true";
  });
  const [showWeekend, setShowWeekend] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;

    return window.localStorage.getItem(SHOW_WEEKEND_STORAGE_KEY) === "true";
  });
  const [vehicleScrollLeft, setVehicleScrollLeft] = useState<number>(0);
  const [vehicleMaxScrollLeft, setVehicleMaxScrollLeft] = useState<number>(0);
  const [isVehicleDragging, setIsVehicleDragging] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState("Newest to Oldest");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string | undefined>(undefined);
  const [selectedTrip, setSelectedTrip] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<ILead | undefined>(undefined);
  const [distances, setDistances] = useState<Distances>();

  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [assignDriverTrip, setAssignDriverTrip] = useState<ICalendarTrip>();
  const vehicleGridRef = useRef<HTMLDivElement>(null);
  const vehicleDragRef = useRef({
    isDragging: false,
    pointerId: 0,
    startX: 0,
    scrollLeft: 0,
  });
  const vehicleDragMovedRef = useRef(false);

  const searchDebounced = useDebounce([searchTerm], 500);

  const { leadsWithFilters: unbookedLeads } = useLeadsWithFilters({
    query: searchDebounced[0],
    pageFilter: 1,
  }, "TripManager");
  // const { leadsWithFilters: uncollectedLeads } = useLeadsWithFilters({ query: "", pageFilter: 2 }); //Replaced with leadsFromIds
  const { leadsFromIds } = useLeadsFromIds(
    calendarTrips.isLoading
      ? []
      : calendarTrips.data?.data
        .map((trip) => trip.trips)
        .flat()
        .map((x) => x.leads)
        .flat()!,
    "TripManager"
  );

  useEffect(() => {
    setInfo({
      name: "Trip Manager",
      desc: "View and manage your lead collections in a trip view",
    });
  }, []);

  useEffect(() => {
    window.localStorage.setItem(
      UNSCHEDULED_PANEL_STORAGE_KEY,
      showUnscheduledAtTop.toString()
    );
  }, [showUnscheduledAtTop]);

  useEffect(() => {
    window.localStorage.setItem(SHOW_WEEKEND_STORAGE_KEY, showWeekend.toString());
  }, [showWeekend]);

  useEffect(() => {
    if (
      !unbookedLeads.isLoading &&
      unbookedLeads.data &&
      !leadsFromIds.isLoading &&
      leadsFromIds.data &&
      !trucks.isLoading &&
      trucks.data &&
      !calendarTrips.isLoading &&
      calendarTrips.data
    ) {
      let _unbookedLeads = sortBy(unbookedLeads.data?.data.leads!);

      let dayLeads: ILead[] = [];

      // Trip selection narrows the map; clearing it falls back to the whole selected day.
      if (selectedTrip !== undefined) {
        dayLeads = leadsFromIds.data?.data.leads.filter(
          (lead: ILead) => lead.tripId === selectedTrip
        )!;
      } else if (selectedDay !== undefined) {
        dayLeads = leadsFromIds.data?.data.leads.filter(
          (lead: ILead) => lead.vehicle?.scheduledCollectionOn === selectedDay
        )!;
      }

      setDayLeads(dayLeads);
      setUnbooked(_unbookedLeads);
      setTrips(calendarTrips.data.data);
      setIsLoading(false);
    }
  }, [
    unbookedLeads.isLoading,
    unbookedLeads.data,
    leadsFromIds.isLoading,
    leadsFromIds.data,
    trucks.isLoading,
    trucks.data,
    calendarTrips.isLoading,
    calendarTrips.data,
    weekNumber,
    searchTerm,
    currentFilter,
    selected,
    selectedDay,
    selectedTrip,
    refresh,
  ]);

  const visibleTrips = useMemo(() => {
    if (showWeekend) return trips;
    return trips.filter((d) => {
      const wd = DateTime.fromISO(d.date).weekday;
      return wd !== 6 && wd !== 7;
    });
  }, [trips, showWeekend]);

  useEffect(() => {
    if (visibleTrips.length === 0) return;
    if (selectedDay && visibleTrips.some((d) => d.date === selectedDay)) return;

    const todayIso = DateTime.now().toISODate();
    const todayInWeek = visibleTrips.find(
      (d) => DateTime.fromISO(d.date).toISODate() === todayIso
    );
    const firstWithTrips = visibleTrips.find((d) => d.trips.length > 0);

    setSelectedDay(todayInWeek?.date ?? firstWithTrips?.date ?? visibleTrips[0].date);
  }, [visibleTrips, selectedDay]);

  const sortBy = (leads: ILead[]) => {
    if (currentFilter === "Postcode A-Z") {
      return leads?.sort((a, b) => {
        return getLeadPostcode(a) > getLeadPostcode(b) ? 1 : -1;
      });
    } else if (currentFilter === "VRM A-Z") {
      return leads?.sort((a, b) => {
        return a.vehicle?.vrm! > b.vehicle?.vrm! ? 1 : -1;
      });
    } else if (currentFilter === "Oldest to Newest") {
      return leads?.sort((a, b) => {
        return new Date(a.created!).getTime() >= new Date(b.created!).getTime()
          ? 1
          : -1;
      });
    } else if (currentFilter === "Newest to Oldest") {
      return leads?.sort((a, b) => {
        return new Date(a.created!).getTime() <= new Date(b.created!).getTime()
          ? 1
          : -1;
      });
    } else {
      return leads;
    }
  };

  const getPostcodeAreaCounts = () => {
    type PostcodeCounts = { unscheduled: number; scheduled: number };

    const counts: Record<string, PostcodeCounts> = {};

    const bump = (
      postcode: string | null | undefined,
      key: keyof PostcodeCounts
    ) => {
      const area = postcode?.slice(0, 2);
      if (!area) return;
      if (!counts[area]) counts[area] = { unscheduled: 0, scheduled: 0 };
      counts[area][key] += 1;
    };

    unbooked?.forEach((lead) => bump(getLeadPostcode(lead), "unscheduled"));

    const weekDates = new Set(trips.map((d) => d.date));
    const scheduledLeads: ILead[] = leadsFromIds.data?.data.leads ?? [];
    scheduledLeads
      .filter(
        (lead) =>
          lead.vehicle?.scheduledCollectionOn &&
          weekDates.has(lead.vehicle.scheduledCollectionOn)
      )
      .forEach((lead) => bump(getLeadPostcode(lead), "scheduled"));

    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  };

  const handleSelectDay = (day: string) => {
    setSelectedDay(day);
    setSelectedTrip(undefined);
  };

  const handleSelectTrip = (_day?: string, trip?: string) => {
    setSelectedTrip((current) => (current === trip ? undefined : trip));
  };

  const handleSelected = (lead: ILead) => {
    if (lead === undefined || selected?.id === lead.id) {
      setSelected(undefined);
      setDistances(undefined);
      return;
    } else {
      setSelected(lead);
    }

    allDistances.mutate(lead.id, {
      onSuccess: (data: any) => {
        setDistances(data.data);
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const handleWeekChange = (next: boolean) => {
    if (next) {
      if (weekNumber === 52) {
        setYearNumber(yearNumber + 1);
        setWeekNumber(1);
      } else {
        setWeekNumber(weekNumber + 1);
      }
    } else {
      if (weekNumber === 1) {
        setYearNumber(yearNumber - 1);
        setWeekNumber(52);
      } else {
        setWeekNumber(weekNumber - 1);
      }
    }
    setSelectedDay(undefined);
    setSelectedTrip(undefined);
  };

  const handleDayShift = (next: boolean) => {
    if (!selectedDay || visibleTrips.length === 0) return;
    const idx = visibleTrips.findIndex((d) => d.date === selectedDay);
    if (idx < 0) return;

    const nextIdx = next ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= visibleTrips.length) {
      handleWeekChange(next);
      return;
    }
    setSelectedDay(visibleTrips[nextIdx].date);
    setSelectedTrip(undefined);
  };

  const getTodaysEvents = () => {
    //filter the events to only include the current day
    return leadsFromIds.data?.data.leads?.filter(
      (lead: ILead) => lead.vehicle?.scheduledCollectionOn === selectedDay
    );
  };

  const handleCollectionNotes = () => {
    setIsCollectionNoteLoading(true);
    const todaysEvents = getTodaysEvents();

    let guids: any = [];

    todaysEvents?.forEach((lead: ILead) => {
      guids.push(lead?.id);
    });

    if (guids.length === 0) {
      addNotification({
        variant: "warn",
        primaryText: "No leads to print",
      });
      setIsCollectionNoteLoading(false);
      return;
    }

    let body = {
      leads: guids,
    };

    getAllCollectionNotes.mutate(body, {
      onSuccess: (file) => {
        const url = window.URL.createObjectURL(file);
        const w = window.open(url, "_blank");
        w?.print();
      },
      onError: (error) => {
        console.log(error);
      },
      onSettled: () => {
        setIsCollectionNoteLoading(false);
      },
    });
  };

  const handleAddTrip = (date: string, truckId: string) => {
    let body = {
      date: date,
      truckId: truckId,
    };

    create.mutate(body, {
      onSuccess: () => {
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
      onSettled: () => {
        setRefresh(!refresh);
      },
    });
  };

  const handleNotesOpen = (
    truck: ITruck,
    date: string,
    unavailableDates: IUnavailableDate[],
    driverId?: string
  ) => {
    setNotesOpen(true);
    setNotesDate(date);
    setNotesUnavailableDates(unavailableDates);
    setNotesTruck(truck);
    setNotesDriverId(driverId);
  };

  const truckHasAlert = (notes: INote[], truck: ITruck) => {
    const driverNotes = notes.filter(
      (note) =>
        note.resourceType === ResourceType.Driver &&
        note.resourceId === truck.driverId
    );
    const truckNotes = notes.filter(
      (note) =>
        note.resourceType === ResourceType.Truck && note.resourceId === truck.id
    );
    return driverNotes.length > 0 || truckNotes.length > 0;
  };

  const handleAssignDriverOpen = (trip: ICalendarTrip) => {
    setAssignDriverTrip(trip);
    setAssignDriverOpen(true);
  };

  const trucksList = useMemo(
    () =>
      (trucks.data?.data ?? [])
        .filter((t) => t.active)
        .sort((a, b) => {
          if (a.thirdParty !== b.thirdParty) {
            return a.thirdParty ? 1 : -1;
          }

          return a.displayOrder - b.displayOrder;
        }),
    [trucks.data]
  );

  const trucksById = useMemo(
    () => new Map(trucksList.map((t) => [t.id, t])),
    [trucksList]
  );

  const canMoveVehiclesLeft = vehicleScrollLeft > 1;
  const canMoveVehiclesRight = vehicleScrollLeft < vehicleMaxScrollLeft - 1;

  const selectedDayData = useMemo(
    () => trips.find((d) => d.date === selectedDay),
    [trips, selectedDay]
  );

  const tripsByTruck = useMemo(() => {
    const map = new Map<string, ICalendarTrip[]>();
    trucksList.forEach((t) => map.set(t.id, []));
    const thirdParty: ICalendarTrip[] = [];

    (selectedDayData?.trips ?? []).forEach((trip) => {
      if (map.has(trip.truckId)) {
        map.get(trip.truckId)!.push(trip);
      } else if (!trucksById.has(trip.truckId)) {
        thirdParty.push(trip);
      }
    });

    map.set(THIRD_PARTY_KEY, thirdParty);
    return map;
  }, [selectedDayData, trucksList, trucksById]);

  useEffect(() => {
    const grid = vehicleGridRef.current;
    if (!grid) return;

    setVehicleScrollLeft(grid.scrollLeft);
    setVehicleMaxScrollLeft(Math.max(grid.scrollWidth - grid.clientWidth, 0));
  }, [trucksList.length, showUnscheduledAtTop, selectedDayData]);

  const tripRowCount = useMemo(() => {
    let max = MIN_TRIP_ROWS;
    tripsByTruck.forEach((list) => {
      if (list.length > max) max = list.length;
    });
    return max;
  }, [tripsByTruck]);

  const allDayLeads = useMemo(
    () =>
      leadsFromIds.data?.data.leads?.filter(
        (lead: ILead) => lead.vehicle?.scheduledCollectionOn === selectedDay
      ) ?? [],
    [leadsFromIds.data, selectedDay]
  );

  const truckStats = (truckId: string) => {
    const truckTrips = tripsByTruck.get(truckId) ?? [];
    const truckTripIds = new Set(truckTrips.map((t) => t.id));
    const truck = trucksById.get(truckId);

    const sourceLeads = truck?.thirdParty
      ? leadsFromIds.data?.data.leads ?? []
      : allDayLeads;
    const leads = sourceLeads.filter((l: ILead) => l.tripId && truckTripIds.has(l.tripId));
    const collected = leads.filter((l: ILead) => !!l.vehicle?.collectedOn).length;
    const cancelled = leads.filter((l: ILead) => l.status === LeadStatus["Cancelled"]).length;
    const capacity = (truck?.capacity ?? 0) * Math.max(truckTrips.length, 1);
    const assigned = leads.length;

    return { assigned, capacity, collected, cancelled };
  };

  const dayStatus = (day: ITripsResponse): DayStatus => {
    const leadIds = day.trips
      .filter((t) => !trucksById.get(t.truckId)?.thirdParty)
      .flatMap((t) => t.leads);
    if (leadIds.length === 0) return "empty";

    if (selected && distances) {
      if (leadIds.some((id) => distances.green?.includes(id))) return "nearbyGreen";
      if (leadIds.some((id) => distances.amber?.includes(id))) return "nearbyAmber";
      if (leadIds.some((id) => distances.red?.includes(id))) return "nearbyRed";
      return "nearbyNone";
    }

    const leads =
      leadsFromIds.data?.data.leads?.filter((l: ILead) => leadIds.includes(l.id)) ?? [];

    if (leads.length === 0) return "none";

    const collected = leads.filter((l: ILead) => !!l.vehicle?.collectedOn).length;
    if (collected === 0) return "none";
    if (collected >= leads.length) return "complete";
    return "partial";
  };

  const handleVehicleScroll = () => {
    const grid = vehicleGridRef.current;
    if (!grid) return;

    setVehicleScrollLeft(grid.scrollLeft);
    setVehicleMaxScrollLeft(Math.max(grid.scrollWidth - grid.clientWidth, 0));
  };

  const handleVehicleDragStart = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;

    const grid = vehicleGridRef.current;
    if (!grid) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    vehicleDragRef.current = {
      isDragging: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: grid.scrollLeft,
    };
    vehicleDragMovedRef.current = false;
    setIsVehicleDragging(true);
  };

  const handleVehicleDragMove = (event: PointerEvent<HTMLElement>) => {
    if (
      !vehicleDragRef.current.isDragging ||
      vehicleDragRef.current.pointerId !== event.pointerId
    ) return;

    const grid = vehicleGridRef.current;
    if (!grid) return;

    const delta = event.clientX - vehicleDragRef.current.startX;
    if (Math.abs(delta) > 3) {
      vehicleDragMovedRef.current = true;
    }

    grid.scrollLeft = vehicleDragRef.current.scrollLeft - delta;
    handleVehicleScroll();
  };

  const handleVehicleDragEnd = (event: PointerEvent<HTMLElement>) => {
    if (
      event.currentTarget.hasPointerCapture(vehicleDragRef.current.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(vehicleDragRef.current.pointerId);
    }

    setIsVehicleDragging(false);
    vehicleDragRef.current.isDragging = false;
  };

  const scrollVehicles = (next: boolean) => {
    const grid = vehicleGridRef.current;
    if (!grid) return;

    grid.scrollBy({
      left: next ? VEHICLE_COLUMN_WIDTH : -VEHICLE_COLUMN_WIDTH,
      behavior: "smooth",
    });
  };

  const vehicleHeaderDragHandlers = {
    onPointerDown: handleVehicleDragStart,
    onPointerMove: handleVehicleDragMove,
    onPointerUp: handleVehicleDragEnd,
    onPointerCancel: handleVehicleDragEnd,
  };

  if (isLoading) {
    return <LoadingWheel />;
  }

  const weekRange =
    trips.length > 0
      ? `${formatDateTime(trips[0].date, DateTime.DATE_MED)} - ${formatDateTime(
        trips[trips.length - 1].date,
        DateTime.DATE_MED
      )}`
      : "";

  const thirdPartyTrips = tripsByTruck.get(THIRD_PARTY_KEY) ?? [];
  const thirdPartyAssigned = thirdPartyTrips.reduce(
    (sum, t) => sum + (t.leads?.length ?? 0),
    0
  );
  const thirdPartyLeads = allDayLeads.filter(
    (l: ILead) => l.tripId && thirdPartyTrips.some((t) => t.id === l.tripId)
  );
  const thirdPartyCollected = thirdPartyLeads.filter(
    (l: ILead) => !!l.vehicle?.collectedOn
  ).length;
  const hasOrphanTrips = thirdPartyTrips.length > 0;

  const vehicleArrowCss = "bg-primary-600 hover:bg-primary-600 text-white w-7 h-full flex items-center justify-center opacity-90 hover:opacity-100 disabled:opacity-25 disabled:cursor-not-allowed z-20 p-0 m-0 transition-all ease-in-out duration-300";

  return (
    <>
      <ConfirmModal
        open={!!addTripConfirm}
        setOpen={(open: boolean) => { if (!open) setAddTripConfirm(null); }}
        title="Truck Unavailable"
        message={`This truck has been marked as unavailable:\n\n${addTripConfirm?.message ?? ""}\n\nAre you sure you want to create a trip anyway?`}
        confirmButtonText="Create Trip Anyway"
        onConfirm={() => {
          if (addTripConfirm) {
            handleAddTrip(addTripConfirm.date, addTripConfirm.truckId);
            setAddTripConfirm(null);
          }
        }}
        isLoading={false}
        Icon={<ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />}
        iconBg="bg-amber-100"
      />
      <AssignDriverModal
        open={assignDriverOpen}
        setOpen={setAssignDriverOpen}
        year={yearNumber}
        week={weekNumber}
        trip={assignDriverTrip}
      />
      <TripManagerNotes
        open={notesOpen}
        setOpen={setNotesOpen}
        truck={notesTruck}
        driverId={notesDriverId}
        date={notesDate!}
        unavailableDates={notesUnavailableDates}
        year={yearNumber}
        week={weekNumber}
      />

      <div className="mt-2 grid grid-cols-12 gap-3">
        {showUnscheduledAtTop && (
          <div className="col-span-12">
            <UnscheduledLeadsPanel
              isTop
              showUnscheduledAtTop={showUnscheduledAtTop}
              unbooked={unbooked}
              selected={selected}
              handleSelected={handleSelected}
              year={yearNumber}
              week={weekNumber}
              currentFilter={currentFilter}
              setCurrentFilter={setCurrentFilter}
              setSearchTerm={setSearchTerm}
              showCollected={showCollected}
              setShowCollected={setShowCollected}
              showNotPaid={showNotPaid}
              setShowNotPaid={setShowNotPaid}
              setShowUnscheduledAtTop={setShowUnscheduledAtTop}
            />
          </div>
        )}

        {!showUnscheduledAtTop && (
          <aside className="col-span-12 lg:col-span-3 xl:col-span-2">
            <UnscheduledLeadsPanel
              isTop={false}
              showUnscheduledAtTop={showUnscheduledAtTop}
              unbooked={unbooked}
              selected={selected}
              handleSelected={handleSelected}
              year={yearNumber}
              week={weekNumber}
              currentFilter={currentFilter}
              setCurrentFilter={setCurrentFilter}
              setSearchTerm={setSearchTerm}
              showCollected={showCollected}
              setShowCollected={setShowCollected}
              showNotPaid={showNotPaid}
              setShowNotPaid={setShowNotPaid}
              setShowUnscheduledAtTop={setShowUnscheduledAtTop}
            />
          </aside>
        )}

        <section
          className={classNames(
            showUnscheduledAtTop
              ? "col-span-12"
              : "col-span-12 lg:col-span-9 xl:col-span-10"
          )}
        >

          <div className="border flex justify-center py-2 border-gray-300 rounded-md bg-white">
            <div className="flex flex-col items-center gap-y-1">
              <div className="flex items-center gap-x-3 text-xs uppercase tracking-wide text-gray-500">
                <span>Area Summary &middot; {weekRange}</span>
                <span className="flex items-center gap-x-1 normal-case tracking-normal text-amber-600">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  Unscheduled
                </span>
                <span className="flex items-center gap-x-1 normal-case tracking-normal text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Scheduled
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-gray-700">
                {getPostcodeAreaCounts().map(([area, { unscheduled, scheduled }]) => (
                  <span key={area} className="flex items-center gap-x-1">
                    <strong className="text-gray-800">{area}:</strong>
                    <Tooltip content={`${unscheduled} unscheduled in ${area}`}>
                      <span className="font-semibold text-amber-600 cursor-help">
                        {unscheduled}
                      </span>
                    </Tooltip>
                    <span className="text-gray-400">/</span>
                    <Tooltip content={`${scheduled} scheduled in ${area}`}>
                      <span className="font-semibold text-emerald-700 cursor-help">
                        {scheduled}
                      </span>
                    </Tooltip>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-2 border border-gray-300 rounded-md bg-white">
            <div
              className="grid divide-x divide-gray-200"
              style={{ gridTemplateColumns: `repeat(${visibleTrips.length}, minmax(0, 1fr))` }}
            >
              {visibleTrips.map((day) => {
                const status = dayStatus(day);
                const isActive = selectedDay === day.date;
                const proximityBg = dayStatusPillBg[status];

                return (
                  <Tooltip
                    key={day.date}
                    content={dayStatusLabel[status]}
                    position="bottom"
                    className="!block"
                  >
                    <button
                      onClick={() => handleSelectDay(day.date)}
                      className={classNames(
                        "w-full flex items-center justify-center gap-x-2 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-primary-200 text-primary-800 font-semibold"
                          : proximityBg ?? "hover:bg-gray-50"
                      )}
                    >
                      <span>
                        {DateTime.fromISO(day.date).toFormat("ccc")} {DateTime.fromISO(day.date).toFormat("d/M")}
                      </span>
                      <span
                        className={classNames(
                          "h-3 w-3 rounded-full",
                          dayStatusDot[status]
                        )}
                      />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          <div className="mt-2 border border-gray-300 rounded-md bg-white flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-x-2">
              <ButtonInput
                isSubmit={false}
                onClick={() => handleWeekChange(false)}
                classes="!px-2 !py-1"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </ButtonInput>
              <button
                onClick={() => setShowWeekend((v) => !v)}
                className={classNames(
                  "text-xs px-2 py-1 rounded border transition-colors",
                  showWeekend
                    ? "bg-primary-100 border-primary-400 text-primary-700"
                    : "bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200"
                )}
              >
                {showWeekend ? "Hide weekends" : "Show weekends"}
              </button>
            </div>
            <div className="flex items-center gap-x-4">
              <h3 className="text-base font-semibold text-gray-800">
                {selectedDay
                  ? formatDateTime(selectedDay, DateTime.DATE_HUGE)
                  : "-"}
              </h3>
              {(() => {
                const total = allDayLeads.length;
                const collected = allDayLeads.filter((l: ILead) => !!l.vehicle?.collectedOn).length;
                const cancelled = allDayLeads.filter((l: ILead) => l.status === LeadStatus["Cancelled"]).length;
                const outstanding = Math.max(total - collected - cancelled, 0);
                const sameDay = allDayLeads.filter((l: ILead) => l.fastPayment && !hasCollectionOption(l)).length;
                const roadside = allDayLeads.filter((l: ILead) => l.roadSidePayment && !hasCollectionOption(l)).length;
                const collectionOptions = allDayLeads.filter((l: ILead) => hasCollectionOption(l)).length;

                return (
                  <div className="flex items-center gap-x-3 text-sm">
                    <Tooltip content="Total leads scheduled to be collected today">
                      <span className="flex items-center gap-x-1 text-gray-700 cursor-help">
                        <span className="h-2 w-2 rounded-full bg-gray-500" />
                        Due <span className="font-semibold">{total}</span>
                      </span>
                    </Tooltip>
                    <span className="text-gray-300">.</span>
                    <Tooltip content="Leads marked as collected">
                      <span className="flex items-center gap-x-1 text-emerald-700 cursor-help">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        Collected <span className="font-semibold">{collected}</span>
                      </span>
                    </Tooltip>
                    <span className="text-gray-300">.</span>
                    <Tooltip content="Leads still to collect (excludes cancelled)">
                      <span className="flex items-center gap-x-1 text-amber-700 cursor-help">
                        <span className="h-2 w-2 rounded-full bg-amber-500" />
                        Outstanding <span className="font-semibold">{outstanding}</span>
                      </span>
                    </Tooltip>
                    {cancelled > 0 && (
                      <>
                        <span className="text-gray-300">.</span>
                        <Tooltip content="Leads cancelled today">
                          <span className="flex items-center gap-x-1 text-red-700 cursor-help">
                            <span className="h-2 w-2 rounded-full bg-red-500" />
                            Cancelled <span className="font-semibold">{cancelled}</span>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    <span className="text-gray-300 hidden md:inline">|</span>
                    <Tooltip content="Same Day Payment leads scheduled today">
                      <span className="flex items-center gap-x-1 text-green-700 cursor-help">
                        <FontAwesomeIcon
                          icon={faHourglass}
                          className="text-green-600"
                        />
                        Same Day <span className="font-semibold">{sameDay}</span>
                      </span>
                    </Tooltip>
                    <span className="text-gray-300">.</span>
                    <Tooltip content="Roadside Payment leads scheduled today">
                      <span className="flex items-center gap-x-1 text-green-700 cursor-help">
                        <FontAwesomeIcon
                          icon={faRoad}
                          className="text-green-600"
                        />
                        Roadside <span className="font-semibold">{roadside}</span>
                      </span>
                    </Tooltip>
                    {collectionOptions > 0 && (
                      <>
                        <span className="text-gray-300">.</span>
                        <Tooltip content="Leads with a widget collection option scheduled today">
                          <span className="flex items-center gap-x-1 text-green-700 cursor-help">
                            <FontAwesomeIcon
                              icon={faRoad}
                              className="text-green-600"
                            />
                            Options <span className="font-semibold">{collectionOptions}</span>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center gap-x-2">
              <ButtonInput
                isSubmit={false}
                onClick={() => handleWeekChange(true)}
                classes="!px-2 !py-1"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </ButtonInput>
            </div>
          </div>

          <div className="mt-2 border border-gray-300 rounded-md bg-white relative overflow-hidden">
            {selectedDayData ? (
              (() => {
                const truckMeta = trucksList.map((truck) => {
                  const unavailableNotes =
                    selectedDayData.unavailableDates.filter(
                      (date: IUnavailableDate) =>
                        date.resourceId === truck.id ||
                        date.resourceId === truck.driverId
                    );
                  return {
                    truck,
                    unavailableNotes,
                    unavailable: unavailableNotes.length > 0,
                    hasAlert: truckHasAlert(selectedDayData.notes, truck),
                    stats: truckStats(truck.id),
                    truckTrips: tripsByTruck.get(truck.id) ?? [],
                  };
                });

                const gridTemplateColumns = `4rem repeat(${trucksList.length}, 15rem)${hasOrphanTrips ? " 15rem" : ""}`;

                return (
                  <>
                    {vehicleMaxScrollLeft > 0 && (
                      <div className="flex justify-between absolute top-0 left-0 w-full h-[90px] pointer-events-none">
                        <button
                          onClick={() => scrollVehicles(false)}
                          className={classNames(vehicleArrowCss, "pointer-events-auto")}
                          disabled={!canMoveVehiclesLeft}
                        >
                          <ChevronLeftIcon className="w-6" />
                          <span className="sr-only">Previous vehicles</span>
                        </button>
                        <button
                          onClick={() => scrollVehicles(true)}
                          className={classNames(vehicleArrowCss, "pointer-events-auto")}
                          disabled={!canMoveVehiclesRight}
                        >
                          <ChevronRightIcon className="w-6" />
                          <span className="sr-only">Next vehicles</span>
                        </button>
                      </div>
                    )}
                    <div
                      ref={vehicleGridRef}
                      onScroll={handleVehicleScroll}
                      className="overflow-x-auto"
                    >
                      <div
                        className="grid"
                        style={{ gridTemplateColumns }}
                      >
                        <div
                          className={classNames(
                            isVehicleDragging ? "cursor-grabbing" : "cursor-grab",
                            "sticky left-0 z-10 bg-gray-50 px-3 py-2 flex items-end text-xs font-semibold text-gray-500 border-b border-r border-gray-300 select-none"
                          )}
                          {...vehicleHeaderDragHandlers}
                        >
                          Trips
                        </div>
                        {truckMeta.map(({ truck, unavailable, hasAlert, stats, unavailableNotes }) => {
                          const handleClick = () => {
                            if (vehicleDragMovedRef.current) return;
                            handleNotesOpen(
                              truck,
                              selectedDayData.date,
                              unavailableNotes,
                              (tripsByTruck.get(truck.id) ?? []).find((t) => !!t.driverId)?.driverId ?? truck.driverId ?? undefined
                            );
                          };

                          const headerContent = (
                            <>
                              <div className="flex items-center gap-x-1 w-full">
                                <span className="text-sm font-semibold text-gray-800 truncate">
                                  {truck.name}
                                </span>
                                {hasAlert && (
                                  <BellIcon className="h-4 w-4 text-red-500" />
                                )}
                                {unavailable && (
                                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                )}
                              </div>
                              {truck.vehicle?.vrm && (
                                <span className="text-xs text-gray-500">
                                  {truck.vehicle?.vrm}
                                </span>
                              )}
                              <span className="text-xs text-gray-700">
                                Capacity {stats.assigned}/{stats.capacity}
                              </span>
                              <span className="text-xs text-gray-700">
                                Collected {stats.collected}
                                {stats.cancelled > 0
                                  ? ` / ${stats.cancelled} Cancelled`
                                  : ""}
                              </span>
                            </>
                          );

                          if (unavailable) {
                            return (
                              <Tooltip
                                key={`h-${truck.id}`}
                                content={
                                  <div className="max-w-48">
                                    <p className="font-semibold">Truck Unavailable</p>
                                    {unavailableNotes.map((note, i) =>
                                      note.message ? <p key={i} className="mt-1 font-normal">{note.message}</p> : null
                                    )}
                                  </div>
                                }
                                position="bottom"
                                className="block"
                              >
                                <button
                                  onClick={handleClick}
                                  className={classNames(
                                    isVehicleDragging ? "cursor-grabbing" : "cursor-grab",
                                    "w-full px-3 py-2 flex flex-col items-start justify-center gap-y-0.5 border-b border-r border-gray-300 text-left transition-colors select-none bg-gray-200"
                                  )}
                                  {...vehicleHeaderDragHandlers}
                                >
                                  {headerContent}
                                </button>
                              </Tooltip>
                            );
                          }

                          return (
                            <button
                              key={`h-${truck.id}`}
                              onClick={handleClick}
                              className={classNames(
                                isVehicleDragging ? "cursor-grabbing" : "cursor-grab",
                                "px-3 py-2 flex flex-col items-start justify-center gap-y-0.5 border-b border-r border-gray-300 text-left transition-colors select-none bg-gray-50 hover:bg-gray-100"
                              )}
                              {...vehicleHeaderDragHandlers}
                            >
                              {headerContent}
                            </button>
                          );
                        })}
                        {hasOrphanTrips && (
                          <div
                            className={classNames(
                              isVehicleDragging ? "cursor-grabbing" : "cursor-grab",
                              "px-3 py-2 flex flex-col items-start justify-center gap-y-0.5 border-b border-gray-300 bg-gray-50 select-none"
                            )}
                            {...vehicleHeaderDragHandlers}
                          >
                            <span className="text-sm font-semibold text-gray-800">
                              Unknown Truck
                            </span>
                            <span className="text-xs text-gray-700">
                              Assigned {thirdPartyAssigned}
                            </span>
                            <span className="text-xs text-gray-700">
                              Collected {thirdPartyCollected}
                            </span>
                          </div>
                        )}

                        {Array.from({ length: tripRowCount }).map((_, rowIdx) => (
                          <Fragment key={`row-${rowIdx}`}>
                            <div className="sticky left-0 z-10 bg-gray-50 px-3 py-2 flex items-center text-xs font-semibold text-gray-500 border-b border-r border-gray-200">
                              Trip {rowIdx + 1}
                            </div>
                            {truckMeta.map(({ truck, truckTrips, unavailable, unavailableNotes }) => {
                              const trip = truckTrips[rowIdx];

                              if (truck.thirdParty) {
                                if (rowIdx > 0) return <Fragment key={`${truck.id}-${rowIdx}`} />;

                                const thirdPartyTrip = truckTrips[0];

                                return (
                                  <div
                                    key={`${truck.id}-${rowIdx}`}
                                    className="border-b border-r border-gray-200 min-h-[11rem]"
                                    style={{ gridRow: `span ${tripRowCount}` }}
                                  >
                                    {thirdPartyTrip ? (
                                      <ThirdPartyTripSlot
                                        date={selectedDayData.date}
                                        leadsFromIds={leadsFromIds.data}
                                        trip={thirdPartyTrip}
                                        capacity={truck.capacity}
                                        distances={distances}
                                        selectedTrip={selectedTrip}
                                        setSelectedTrip={handleSelectTrip}
                                        handleSelected={handleSelected}
                                        year={yearNumber}
                                        week={weekNumber}
                                        searchTerm={searchTerm}
                                        showCollectedFilter={showCollected}
                                        showNotPaidFilter={showNotPaid}
                                      />
                                    ) : (
                                      <div className="h-full flex items-center justify-center text-sm text-gray-400">
                                        No third party trip
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              const cellInner = trip ? (
                                <TripSlot
                                  date={selectedDayData.date}
                                  leadsFromIds={leadsFromIds.data}
                                  trip={trip}
                                  capacity={truck.capacity}
                                  distances={distances}
                                  selectedTrip={selectedTrip}
                                  setSelectedTrip={handleSelectTrip}
                                  handleSelected={handleSelected}
                                  unavailable={unavailable}
                                  year={yearNumber}
                                  week={weekNumber}
                                  searchTerm={searchTerm}
                                  showCollectedFilter={showCollected}
                                  showNotPaidFilter={showNotPaid}
                                  onDriverAvatarClick={handleAssignDriverOpen}
                                />
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <Tooltip
                                    content={unavailable ? (
                                      <div className="max-w-48">
                                        <p className="font-semibold">Truck Unavailable</p>
                                        {unavailableNotes.map((note, i) =>
                                          note.message ? <p key={i} className="mt-1 font-normal">{note.message}</p> : null
                                        )}
                                      </div>
                                    ) : "Add Trip"}
                                    position="bottom"
                                  >
                                    <button
                                      onClick={() => {
                                        if (unavailable) {
                                          const message = unavailableNotes
                                            .map((n) => n.message)
                                            .filter(Boolean)
                                            .join("\n") || "This truck is marked as unavailable on this day.";
                                          setAddTripConfirm({ date: selectedDayData.date, truckId: truck.id, message });
                                        } else {
                                          handleAddTrip(selectedDayData.date, truck.id);
                                        }
                                      }}
                                      className="text-2xl bg-green-500 rounded-full text-white hover:bg-green-700"
                                      title="Add trip"
                                    >
                                      <PlusIcon
                                        className="h-8 w-8"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  </Tooltip>
                                </div>
                              );

                              return (
                                <div
                                  key={`${truck.id}-${rowIdx}`}
                                  className={classNames(
                                    "border-b border-r border-gray-200 min-h-[11rem]",
                                    unavailable ? "bg-gray-100" : ""
                                  )}
                                >
                                  {cellInner}
                                </div>
                              );
                            })}
                            {hasOrphanTrips && (() => {
                              const trip = thirdPartyTrips[rowIdx];
                              return (
                                <div className="border-b border-gray-200 min-h-[11rem]">
                                  {trip ? (
                                    <TripSlot
                                      date={selectedDayData.date}
                                      leadsFromIds={leadsFromIds.data}
                                      trip={trip}
                                      capacity={trip.leads?.length ?? 0}
                                      distances={distances}
                                      selectedTrip={selectedTrip}
                                      setSelectedTrip={handleSelectTrip}
                                      handleSelected={handleSelected}
                                      unavailable={false}
                                      year={yearNumber}
                                      week={weekNumber}
                                      searchTerm={searchTerm}
                                      showCollectedFilter={showCollected}
                                      showNotPaidFilter={showNotPaid}
                                      onDriverAvatarClick={handleAssignDriverOpen}
                                    />
                                  ) : null}
                                </div>
                              );
                            })()}
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()
            ) : (
              <div className="p-6 text-center text-sm text-gray-500">
                No day selected.
              </div>
            )}
          </div>

          <div className="mt-3">
            <CalendarDayMap leads={dayLeads} selectedLead={selected} />
          </div>

          <div className="mt-3 flex gap-x-4">
            {isCollectionNoteLoading ? (
              <div className="w-20">
                <LoadingWheel />
              </div>
            ) : (
              <ButtonInput
                label="Print Collection Notes"
                isSubmit={false}
                onClick={() => handleCollectionNotes()}
                disabled={!selectedDay}
                classes={!selectedDay && "bg-gray-400 hover:bg-gray-400"}
              />
            )}
            <PrintJobSheet
              selectedDay={selectedDay}
              week={weekNumber}
              year={yearNumber}
            />
          </div>
        </section>
      </div>
    </>
  );
}
